import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {Search, Home, Bed, Sofa, Package, MapPin, Euro} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import Navbar from '@/components/Navbar'
import Footer from '@/components/Footer'
import { useAuth } from '@/hooks/useAuth'
import toast from 'react-hot-toast'

interface Anuncio {
  _id: string
  titulo: string
  descricao: string
  categoria: string
  preco: number
  localizacao: string
  fotos?: string[]
  imagem?: string
  userId?: string
  userName?: string
  userPhoto?: string
  usuario?: {
    id: string
    nome: string
    foto: string
  }
  createdAt?: string
  dataCriacao?: string
}

function Marketplace() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const contatar = (anuncio: Anuncio) => {
    const destinatarioId = anuncio.userId || anuncio.usuario?.id
    if (!destinatarioId) { toast.error('Anunciante indisponível.'); return }
    if (!user?.userId) { toast.error('Entre na sua conta e volte ao anúncio para conversar.'); navigate('/cadastro'); return }
    if (destinatarioId === user.userId) { toast('Este anúncio é seu.'); return }
    navigate(`/perfil?tab=chat&contato=${encodeURIComponent(destinatarioId)}`)
  }

  const [categoriaFiltro, setCategoriaFiltro] = useState<string>('todos')
  const [busca, setBusca] = useState('')
  const [anunciosDb, setAnunciosDb] = useState<Anuncio[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchAnuncios = async () => {
      setLoading(true)
      try {
        const { list } = await lumi.entities.marketplace_anuncios.list({
          sort: { createdAt: -1 }
        })
        setAnunciosDb(list || [])
      } catch (error) {
        console.error('Erro ao carregar anuncios:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchAnuncios()
  }, [])

  // Exibe somente anúncios cadastrados; a demonstração utiliza seu próprio armazenamento.
  const todosAnuncios: Anuncio[] = anunciosDb

  const anunciosFiltrados = todosAnuncios.filter(anuncio => {
    const matchCategoria = categoriaFiltro === 'todos' || anuncio.categoria === categoriaFiltro
    const matchBusca = anuncio.titulo.toLowerCase().includes(busca.toLowerCase()) ||
                       anuncio.descricao.toLowerCase().includes(busca.toLowerCase()) ||
                       (anuncio.localizacao || '').toLowerCase().includes(busca.toLowerCase())
    return matchCategoria && matchBusca
  })

  const getCategoriaIcon = (categoria: string) => {
    switch (categoria) {
      case 'quarto': return <Home className="w-5 h-5" />
      case 'cama': return <Bed className="w-5 h-5" />
      case 'apartamento': return <Sofa className="w-5 h-5" />
      case 'desapego': return <Package className="w-5 h-5" />
      default: return <Package className="w-5 h-5" />
    }
  }

  const getCategoriaLabel = (categoria: string) => {
    switch (categoria) {
      case 'quarto': return 'Quarto'
      case 'cama': return 'Cama'
      case 'apartamento': return 'Apartamento'
      case 'desapego': return 'Desapego'
      default: return categoria
    }
  }

  const getImagem = (anuncio: Anuncio): string => {
    if (anuncio.fotos && anuncio.fotos.length > 0) return anuncio.fotos[0]
    if (anuncio.imagem) return anuncio.imagem
    return 'https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?w=800'
  }

  const getUsuarioNome = (anuncio: Anuncio): string => {
    return anuncio.userName || anuncio.usuario?.nome || 'Usuario'
  }

  const getUsuarioFoto = (anuncio: Anuncio): string => {
    return anuncio.userPhoto || anuncio.usuario?.foto || `https://i.pravatar.cc/150?u=${anuncio.userId || anuncio._id}`
  }

  const getUsuarioId = (anuncio: Anuncio): string => {
    return anuncio.userId || anuncio.usuario?.id || anuncio._id
  }

  return (
    <div className="min-h-screen bg-[#111111]">
      <Navbar />

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-[#1a0a00] via-[#2c1a0e] to-[#1a0a00] border-b-2 border-[#5D4037] py-16">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #E53935 0%, transparent 50%), radial-gradient(circle at 80% 50%, #5D4037 0%, transparent 50%)' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-black text-white mb-4">
            Marketplace <span className="text-[#E53935]">BBQ Jobs</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Encontre desapegos e moradia na comunidade da gastronomia brasileira pelo mundo.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Barra de Busca e Filtros */}
        <div className="bg-[#1e1e1e] border border-[#3a2a1e] rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Campo de Busca */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por título, descrição ou localização..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-[#2a2a2a] border-2 border-[#3a2a1e] text-gray-100 placeholder-gray-500 rounded-lg focus:border-[#E53935] focus:outline-none transition-colors"
              />
            </div>

            {/* Filtros de Categoria */}
            <div className="flex gap-2 flex-wrap justify-center">
              {[
                { key: 'todos', label: 'Todos', icon: null },
                { key: 'quarto', label: 'Quartos', icon: <Home className="w-4 h-4" /> },
                { key: 'cama', label: 'Camas', icon: <Bed className="w-4 h-4" /> },
                { key: 'apartamento', label: 'Apartamentos', icon: <Sofa className="w-4 h-4" /> },
                { key: 'desapego', label: 'Desapegos', icon: <Package className="w-4 h-4" /> },
              ].map(({ key, label, icon }) => (
                <button
                  key={key}
                  onClick={() => setCategoriaFiltro(key)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                    categoriaFiltro === key
                      ? 'bg-[#E53935] text-white shadow-lg shadow-[#E53935]/30'
                      : 'bg-[#2a2a2a] text-gray-300 border border-[#3a2a1e] hover:border-[#E53935] hover:text-white'
                  }`}
                >
                  {icon}
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-10 h-10 border-4 border-[#E53935] border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Grid de Anúncios */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {anunciosFiltrados.map((anuncio) => (
              <div
                key={anuncio._id}
                className="bg-[#1e1e1e] border border-[#3a2a1e] rounded-xl overflow-hidden hover:border-[#E53935] hover:shadow-2xl hover:shadow-[#E53935]/10 transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* Imagem */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={getImagem(anuncio)}
                    alt={anuncio.titulo}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute top-3 right-3 bg-[#E53935] text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                    {getCategoriaIcon(anuncio.categoria)}
                    {getCategoriaLabel(anuncio.categoria)}
                  </div>
                </div>

                {/* Conteúdo */}
                <div className="p-5">
                  <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">
                    {anuncio.titulo}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {anuncio.descricao}
                  </p>

                  {/* Localização */}
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                    <MapPin className="w-4 h-4 text-[#E53935]" />
                    {anuncio.localizacao}
                  </div>

                  {/* Preço */}
                  <div className="flex items-center gap-2 mb-4">
                    <Euro className="w-5 h-5 text-[#E53935]" />
                    <span className="text-2xl font-black text-[#E53935]">
                      {anuncio.preco}
                    </span>
                    {['quarto', 'cama', 'apartamento'].includes(anuncio.categoria) && <span className="text-gray-500 text-sm">/mês</span>}
                  </div>

                  {/* Usuário */}
                  <div className="flex items-center justify-between pt-4 border-t border-[#3a2a1e]">
                    <Link
                      to={`/perfil/${encodeURIComponent(getUsuarioId(anuncio))}`}
                      className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                    >
                      <img
                        src={getUsuarioFoto(anuncio)}
                        alt={getUsuarioNome(anuncio)}
                        className="w-8 h-8 rounded-full border-2 border-[#5D4037] object-cover"
                      />
                      <span className="text-sm font-semibold text-gray-300">
                        {getUsuarioNome(anuncio)}
                      </span>
                    </Link>
                    <button onClick={() => contatar(anuncio)} className="bg-gradient-to-r from-[#C62828] to-[#E53935] text-white px-4 py-2 rounded-lg font-semibold hover:shadow-lg hover:shadow-[#E53935]/30 transition-all text-sm">
                      Contatar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Mensagem quando não há resultados */}
        {!loading && anunciosFiltrados.length === 0 && (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-[#5D4037] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-300 mb-2">
              Nenhum anúncio encontrado
            </h3>
            <p className="text-gray-500">
              Tente ajustar os filtros ou a busca para encontrar o que procura.
            </p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  )
}

export default Marketplace
